import { useTranslation } from 'react-i18next';
import { TeeButton } from '@/components/ui/tee-button';
import type { IntakeFixSuggestion } from '@/types/intake';

export interface SuggestedFixCardProps {
  fix: IntakeFixSuggestion;
  onAnswer: (solved: boolean) => void;
  disabled?: boolean;
}

// Copied verbatim from TicketDetailPane.tsx's sectionHeading constant — reuse
// this exact class, do not re-derive it.
const sectionHeading =
  'text-[12px] font-bold uppercase tracking-[.08em] text-[var(--text-content-sub-title)]';

/** FIXMATCH-02: heading, "What Gopher checked" source line, numbered steps, Yes/No. */
export function SuggestedFixCard({ fix, onAnswer, disabled }: SuggestedFixCardProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card-elevated)] px-4 py-4 flex flex-col gap-4">
      <h2 className="text-[20px] font-semibold leading-[1.2] text-[var(--text-content-title)]">
        {t('intake.fixmatch.heading')}
      </h2>

      <div className="flex flex-col gap-1">
        <span className={sectionHeading}>{t('intake.fixmatch.source_label')}</span>
        <p className="text-[15px] leading-[1.5] text-[var(--text-content-title)]">
          {fix.sourceLine}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {fix.steps.length > 1 && (
          <p className="text-[14px] font-semibold leading-[1.4] text-[var(--text-content-title)]">
            {t('intake.fixmatch.steps_intro')}
          </p>
        )}
        <div role="list" className="flex flex-col gap-2">
          {fix.steps.map((step, i) => (
            <div key={`${i}-${step}`} role="listitem" className="flex items-start gap-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-gold-500)] text-[12px] font-semibold text-[var(--text-on-forest)]">
                {i + 1}
              </span>
              <p className="text-[15px] leading-[1.5] text-[var(--text-content-title)]">
                {step}
              </p>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[14px] font-semibold leading-[1.4] text-[var(--text-content-title)]">
        {t('intake.fixmatch.prompt')}
      </p>

      <div className="flex flex-col gap-2 md:flex-row">
        <TeeButton
          variant="primary"
          size="m"
          text={t('intake.fixmatch.yes')}
          disabled={disabled}
          onClick={() => onAnswer(true)}
          className="min-h-[44px] md:min-h-0"
        />
        <TeeButton
          variant="secondary"
          size="m"
          text={t('intake.fixmatch.no')}
          disabled={disabled}
          onClick={() => onAnswer(false)}
          className="min-h-[44px] md:min-h-0"
        />
      </div>
    </div>
  );
}

export default SuggestedFixCard;
